import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  AfterChatEvent,
  AgentEvent,
  BaseEvent,
  ChatEntryEvent,
  ErrorEvent,
  PenStatusEvent,
  QueueEvent
} from '../../types/call-guide/events';
import { ChatSessionState } from '../../types/call-guide/state';

type EstablishedEvent =
  | AgentEvent
  | {
      robot: boolean;
    };

type FinishedEvent = Partial<BaseEvent>;

interface ChatState {
  currentState?: ChatSessionState;
  messages: ChatEntryEvent[];
  agent?: {
    isTyping: boolean;
    alias: string;
  };
  stateInfo: {
    startup?: BaseEvent;
    queue?: QueueEvent;
    established?: { robot: boolean } & Partial<AgentEvent>;
    placedInQueue?: BaseEvent;
    afterChat?: AfterChatEvent;
    finished?: FinishedEvent;
    error?: ErrorEvent;
    entranceClosed?: BaseEvent;
  };
}

const initialState: ChatState = {
  messages: [],
  stateInfo: {}
};

const chatSlice = createSlice({
  name: 'chatState',
  initialState,
  reducers: {
    chatStateFetched(state, action: PayloadAction<ChatSessionState>) {
      state.currentState = action.payload;
    },
    chatSessionStarted(state, action: PayloadAction<BaseEvent>) {
      state.currentState = 'startup';
      state.stateInfo.startup = action.payload;
    },
    chatSessionQueued(state, action: PayloadAction<QueueEvent>) {
      state.currentState = 'queue';
      state.stateInfo.queue = action.payload;
    },
    chatSessionEstablished(state, action: PayloadAction<EstablishedEvent>) {
      state.currentState = 'established';
      if ('robot' in action.payload) {
        state.stateInfo.established = action.payload;
      } else {
        state.stateInfo.established = { ...action.payload, robot: false };
      }
    },
    chatPlacedInQueueByAgent(state, action: PayloadAction<BaseEvent>) {
      state.currentState = 'placedInQueue';
      state.stateInfo.placedInQueue = action.payload;
    },
    chatEnded(state, action: PayloadAction<AfterChatEvent>) {
      state.currentState = 'afterChat';
      state.stateInfo.afterChat = action.payload;
    },
    chatFinished(state, action: PayloadAction<FinishedEvent>) {
      state.currentState = 'finished';
      state.stateInfo.finished = action.payload;
    },
    chatError(state, action: PayloadAction<ErrorEvent>) {
      state.currentState = 'error';
      state.stateInfo.error = action.payload;
    },
    chatEntranceClosed(state, action: PayloadAction<BaseEvent>) {
      state.currentState = 'entranceClosed';
      state.stateInfo.entranceClosed = action.payload;
    },
    chatMessageReceived(state, action: PayloadAction<ChatEntryEvent>) {
      state.messages.push(action.payload);
    },
    clearChatEntries(state) {
      state.messages = [];
    },
    agentIsTyping(state, action: PayloadAction<PenStatusEvent>) {
      state.agent = {
        isTyping: action.payload.penStatus === 'on',
        alias: action.payload.alias
      };
    }
  }
});

export const {
  chatStateFetched,
  chatSessionStarted,
  chatSessionQueued,
  chatPlacedInQueueByAgent,
  chatSessionEstablished,
  chatEnded,
  chatFinished,
  chatError,
  chatEntranceClosed,
  chatMessageReceived,
  clearChatEntries,
  agentIsTyping
} = chatSlice.actions;

export default chatSlice.reducer;
